'use client';

import type { ISnowplowContext, ISnowplowUm } from '@fanduel/snowplow-um-contract';
import { SNOWPLOW_EVENT_SCHEMAS, type SnowplowSchemaKey } from '@fanduel/snowplow-um/schemas';
import type { GameTileType, GameTrackingInfo } from '@/types/games';
import { getGameName } from '@/logic/game-adapter/game-adapter';
import { mapPropsToKeys } from '@/utils/tracking/utils';
import { getPageNameFromPathname } from '@/tracking/context/page-name';

let snowplowInstance: ISnowplowUm | null = null;
let initPromise: Promise<ISnowplowUm> | null = null;

export const EVENT_TYPE = {
    GAME_CLICKED: 'game_clicked',
    TOP_NAV_IMPRESSION: 'top_nav_impression',
    TOP_NAV_SCROLL: 'top_nav_scroll',
    TOP_NAV_SELECTED: 'top_nav_selected',
    ZONE_SCROLLED: 'zone_scrolled',
    ZONE_INITIAL_LOAD: 'zone_initial_load',
    GAME_ZONE_IMPRESSION: 'game_zone_impression',
    GAME_ZONE_SCROLL: 'game_zone_scroll',
    GAME_ZONE_SELECTED: 'game_zone_selected',
    CATEGORY_PAGE_IMPRESSION: 'category_page_impression',
    CATEGORY_PAGE_GAME_SELECTED: 'category_page_game_selected',
    ZONE_INTERACTION: 'zone_interaction',
    CATEGORY_SCREEN_VIEW: 'category_screen_view',
    GAME_FAVORITED: 'game_favorited',
    GAME_FAVORITE_REMOVED: 'game_favorite_removed',
    INFO_ICON_CLICKED: 'info_icon_clicked',
    GAME_INFO_PAGE_VIEWED: 'game_info_page_viewed',
    SEARCH_OPENED: 'search_opened',
    SEARCH_INITIALIZED: 'search_initialized',
    SEARCH_RESULTS_RETURNED: 'search_results_returned',
    SEARCH_RESULTS_SELECTED: 'search_results_selected'
} as const;

export const TOP_NAV_SNOWPLOW_BANNER_PROPS = {
    bannerName: 'top_nav',
    bannerId: 'top_nav',
    bannerType: 'navigation',
    eventCategory: 'navigation'
} as const;

export const SNOWPLOW_CONTEXT_MAPPING = {
    USER_PROPERTIES: 'user_properties',
    GLOBAL_PROPERTIES: 'global_properties',
    GAME_IMPRESSION: 'game_impression',
    NAVIGATION_MENU_ELEMENT: 'navigation_menu_element',
    ZONE_INTERACTION: 'zone_interaction',
    ML_RECOMMENDATION: 'ml_recommendation',
    ML_RECOMMENDATION_ITEM: 'ml_recommendation_item'
} as const;

export const SNOWPLOW_EVENTS_MAPPING = {
    SCHEMAS: SNOWPLOW_EVENT_SCHEMAS,
    CONTEXTS: {
        [SNOWPLOW_CONTEXT_MAPPING.GLOBAL_PROPERTIES]: {
            product: 'product',
            pageName: 'page_name',
            balance: 'balance',
            loginStatus: 'login_status',
            fullUrl: 'full_url',
            hostname: 'hostname',
            pagePath: 'page_path',
            pageTitle: 'page_title',
            siteVersion: 'site_version',
            sitePlatform: 'site_platform',
            jurisdiction: 'jurisdiction'
        },
        [SNOWPLOW_CONTEXT_MAPPING.GAME_IMPRESSION]: {
            position: 'position',
            gameId: 'game_id',
            gameName: 'game_name',
            onScreen: 'on_screen',
            gameTileType: 'game_tile_type'
        },
        [SNOWPLOW_CONTEXT_MAPPING.NAVIGATION_MENU_ELEMENT]: {
            label: 'label',
            module: 'module',
            position: 'position',
            navigationMenuName: 'navigation_menu_name',
            onScreen: 'on_screen',
            pinned: 'pinned',
            personalized: 'personalized'
        },
        [SNOWPLOW_CONTEXT_MAPPING.ZONE_INTERACTION]: {
            action: 'action',
            automatedZone: 'automated_zone',
            ctaLabel: 'cta_label',
            pageName: 'page_name',
            zoneId: 'zone_id',
            zoneLayout: 'zone_layout',
            zoneName: 'zone_name',
            zonePosition: 'zone_position',
            zoneType: 'zone_type'
        },
        [SNOWPLOW_CONTEXT_MAPPING.ML_RECOMMENDATION]: {
            modelName: 'model_name',
            modelVersion: 'model_version',
            modelStatus: 'model_status',
            inferenceTimestampUtc: 'inference_timestamp_utc',
            location: 'location',
            triggerItemId: 'trigger_item_id',
            modelType: 'model_type'
        },
        [SNOWPLOW_CONTEXT_MAPPING.ML_RECOMMENDATION_ITEM]: {
            itemId: 'item_id',
            rankOrder: 'rank_order',
            modelScore: 'model_score'
        }
    }
};

export type { SnowplowSchemaKey };

export type SnowplowContextInput = {
    contextType: SnowplowSchemaKey;
    contextProps: Record<string, unknown>;
};

export const getSnowplowContext = (
    contextType: SnowplowSchemaKey,
    props: Record<string, unknown>
): ISnowplowContext => {
    return {
        schema: SNOWPLOW_EVENTS_MAPPING.SCHEMAS[contextType],
        data: mapPropsToKeys(props, contextType)
    };
};

export const initializeSnowplow = async (): Promise<ISnowplowUm> => {
    if (snowplowInstance) {
        return snowplowInstance;
    }

    if (initPromise) {
        return initPromise;
    }

    initPromise = (async () => {
        try {
            const { SnowplowUm } = await import('@fanduel/snowplow-um');
            const { initializeUnifiedModules } = await import('@/logic/unified-modules');

            await initializeUnifiedModules();
            snowplowInstance = SnowplowUm;

            return SnowplowUm;
        } catch (error) {
            console.error('[SnowplowModule.initialize] Error initializing Snowplow', error);
            initPromise = null;
            throw error;
        }
    })();

    return initPromise;
};

export const getSnowplowSessionId = async (): Promise<string | null> => {
    const snowplow = await initializeSnowplow();
    return snowplow.getClientSession().sessionId || null;
};

export const getNavigationMenuElementData = (
    label: string,
    position: number,
    module: string,
    navigationMenuName: string,
    onScreen: boolean,
    pinned: boolean,
    personalized: boolean
) => ({
    label,
    module,
    position,
    navigationMenuName,
    onScreen,
    pinned,
    personalized
});

/** Snowplow zone fields from components. */
export type GameZoneSnowplowStaticConfig = {
    zoneName: string;
    zoneType: string;
    zoneLayout?: string | null;
    automatedZone?: boolean | null;
};

/**
 * Loose inputs for `game_element` 5-0-0 (camelCase keys match `SNOWPLOW_CONTEXT_MAPPING.GAME_IMPRESSION`).
 * `getGameElementData` fills defaults for required fields and keeps the optional tile type nullable.
 */
export type GameElementDataParams = {
    position?: string | number | null;
    gameId?: string | number | null;
    gameName?: string | null;
    onScreen?: boolean;
    gameTileType?: GameTileType | null;
};

export type GameElementData = {
    position: number;
    gameId: string;
    gameName: string;
    onScreen: boolean;
    gameTileType: GameTileType | null;
};

/**
 * Builds a schema-safe `game_element` 5-0-0 context.
 */
export const getGameElementData = (p: GameElementDataParams): GameElementData => {
    const toStr = (v: string | number | null | undefined): string => String(v ?? '');
    const toInt = (v: string | number | null | undefined): number => {
        const n = Number(v);
        return Number.isFinite(n) ? Math.trunc(n) : 0;
    };

    return {
        position: toInt(p.position),
        gameId: toStr(p.gameId),
        gameName: toStr(p.gameName),
        onScreen: p.onScreen ?? false,
        gameTileType: p.gameTileType ?? null
    };
};

export const getGameElementDataFromZoneGame = (
    slotPosition: number,
    onScreen: boolean,
    game: GameTrackingInfo
): GameElementData =>
    getGameElementData({
        position: slotPosition,
        gameId: game.uid,
        gameName: getGameName(game.data.display_name),
        onScreen,
        gameTileType: game.gameTileType ?? null
    });

type SnowplowEventType = SnowplowSchemaKey;
type SnowplowEventProps = Record<string, unknown>;

export const getSnowplowEvent = (
    event: SnowplowEventType,
    eventProps: SnowplowEventProps,
    contexts: SnowplowContextInput[] = []
) => ({ destination: 'snowplow', event, eventProps, contexts });

export type SnowplowContext = {
    schema: string;
    data: Record<string, any>;
};
export type SnowplowEvent = {
    schema: string;
    data: Record<string, any>;
    context?: SnowplowContext[];
};

export type EventType = (typeof EVENT_TYPE)[keyof typeof EVENT_TYPE];

export const buildSnowplowEvent = (
    eventName: EventType,
    props: Record<string, unknown>,
    contexts: SnowplowContextInput[],
    eventGlobalProperties: any
): SnowplowEvent => {
    const eventProps = mapPropsToKeys(props, eventName);

    const eventContexts = contexts.map(({ contextType, contextProps }) => {
        return getSnowplowContext(contextType, contextProps);
    });

    const globalContext = getSnowplowContext(
        SNOWPLOW_CONTEXT_MAPPING.GLOBAL_PROPERTIES as SnowplowSchemaKey,
        eventGlobalProperties
    );

    return {
        schema: SNOWPLOW_EVENTS_MAPPING.SCHEMAS[eventName],
        data: eventProps,
        context: [globalContext, ...eventContexts]
    };
};

type SnowplowGetterProps = {
    balance?: number;
    isLoggedIn?: boolean;
    region?: string;
    isSearchOpen?: boolean;
    isMobile?: boolean;
    isAndroidWrapper?: boolean;
    isIosWrapper?: boolean;
    pathname?: string;
};

const getProduct = () => {
    const product = process.env.PRODUCT;
    switch (product) {
        case 'casino':
            return 'casino_standalone_lobby';
        case 'poker':
            return 'poker_standalone_lobby';
        case 'xsell':
            return 'casino_xsell_lobby';
        default:
            console.warn('[Amplitude] Unknown product', product);
    }
};

const getAndroidDistribution = () => {
    const ua = (navigator?.userAgent || '').toLowerCase();
    switch (true) {
        case ua.includes('selfdist'):
            return 'side-load';
        case ua.includes('playstore'):
            return 'play-store';
        default:
            return '';
    }
};

const getSiteVersion = ({ isMobile, isWrapper }: { isMobile: boolean; isWrapper: boolean }) => {
    switch (true) {
        case isMobile && !isWrapper:
            return 'mobile_web';
        case isMobile && isWrapper:
            return 'native';
        default:
            return 'desktop';
    }
};

const getSitePlatform = ({
    isMobile,
    isAndroidWrapper,
    isIosWrapper
}: {
    isMobile: boolean;
    isAndroidWrapper: boolean;
    isIosWrapper: boolean;
}) => {
    const isWrapper = isAndroidWrapper || isIosWrapper;
    switch (true) {
        case isMobile && !isWrapper:
            return 'mobile_web';
        case isMobile && isWrapper:
            return isIosWrapper ? 'ios' : 'android';
        default:
            return 'desktop';
    }
};

export const getSnowplowProperties = ({
    balance,
    isLoggedIn,
    region,
    isSearchOpen,
    isMobile,
    isAndroidWrapper,
    isIosWrapper,
    pathname
}: SnowplowGetterProps): any => {
    const eventGlobalProperties: any = {
        fullUrl: window.location.href,
        hostname: window.location.hostname,
        pagePath: pathname ?? window.location.pathname,
        product: getProduct(),
        nationalJurisdiction: process.env.COUNTRY,
        androidDistributionMethod: getAndroidDistribution()
    };

    if (isMobile !== undefined && (isAndroidWrapper !== undefined || isIosWrapper !== undefined)) {
        eventGlobalProperties.siteVersion = getSiteVersion({
            isMobile,
            isWrapper: isAndroidWrapper || (isIosWrapper as boolean)
        });
    }

    if (isMobile !== undefined && isAndroidWrapper !== undefined && isIosWrapper !== undefined) {
        eventGlobalProperties.sitePlatform = getSitePlatform({ isMobile, isAndroidWrapper, isIosWrapper });
    }

    if (isSearchOpen !== undefined) {
        const pagePath = (pathname ?? window.location.pathname) || '/';
        eventGlobalProperties.pageName = isSearchOpen ? 'search' : getPageNameFromPathname(pagePath);
    }

    if (isLoggedIn !== undefined) {
        eventGlobalProperties.loginStatus = isLoggedIn ? 'logged_in' : 'logged_out';
    }

    if (region) {
        eventGlobalProperties.jurisdiction = region?.toLowerCase();
    }

    if (balance) {
        eventGlobalProperties.balance = Math.round(balance);
    }

    const androidDistribution = getAndroidDistribution();
    if (androidDistribution) {
        eventGlobalProperties.androidDistributionMethod = androidDistribution;
    }

    return eventGlobalProperties;
};
